/**
 * RPDM W1-D · Project Workspace Shell.
 *
 * Common layout for the four W1-D workspace surfaces (Summary, Delivery,
 * Team, Settings). Renders sticky header + hybrid sidenav + outlet body.
 *
 * SCOPE: pure layout composition. NO data fetching (children own their
 * data). Owned by W1-D — never absorb business logic.
 *
 * Master feature-flag gating happens BEFORE this shell renders, at the
 * route level in App.js. This component assumes it is only mounted when
 * the master flag is enabled.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "../../../lib/api";
import { PageLoading, PageError } from "../components/ui";
import ProjectStickyHeader from "./ProjectStickyHeader";
import ProjectSideNav from "./ProjectSideNav";
import { toast } from "sonner";

export default function ProjectWorkspaceShell({ children }) {
    const { id } = useParams();
    const [engagement, setEngagement] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setError(false);
            try {
                const r = await api.get(`/projects/engagement/${id}`);
                if (!cancelled) {
                    setEngagement(r.data.engagement);
                }
            } catch (err) {
                if (!cancelled) {
                    setError(true);
                    toast.error(formatApiError(err));
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [id]);

    if (loading) {
        return (
            <PageLoading
                label="Loading workspace…"
                testId="rpdm-workspace-loading"
            />
        );
    }

    if (error || !engagement) {
        return (
            <PageError
                title="Workspace not found"
                message="This engagement may have been removed or you no longer have access."
                backTo="/projects"
                backLabel="Back to Projects"
                testId="rpdm-workspace-not-found"
            />
        );
    }

    // Normalise the engagement shape into the read-only object the shell
    // and its child views consume. This lets children stay decoupled
    // from backend field naming.
    const view = {
        id: engagement.id,
        name: engagement.name,
        code: engagement.engagement_number,
        kind: engagement.kind || engagement.type,
        state: engagement.status,
        raw: engagement,
    };

    return (
        <div className="flex min-h-[calc(100vh-4rem)] flex-col" data-testid="rpdm-workspace-shell">
            <ProjectStickyHeader engagement={view} />
            <div className="flex flex-1">
                <ProjectSideNav />
                <main className="flex-1 overflow-x-auto px-6 py-5" data-testid="rpdm-workspace-body">
                    {typeof children === "function" ? children(view) : children}
                </main>
            </div>
        </div>
    );
}
